import { CommonModule, DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, Inject, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { environment } from '../../../environment/environment';
import { SharedService } from '../../core/Services/shared';

type PaymentState = 'verifying' | 'success' | 'error';

interface VerifyPaymentResponse {
  status?: string;
  message?: string;
  data?: {
    payment_uuid?: string;
    payment_status?: string;
    amount?: number | string;
    currency?: string;
  };
}

@Component({
  selector: 'app-payment-thank-you',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './payment-thank-you.component.html',
  styleUrl: './payment-thank-you.component.scss',
})
export class PaymentThankYouComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly http = inject(HttpClient);
  private readonly shared = inject(SharedService);

  paymentState: PaymentState = 'verifying';
  paymentUuid: string | null = null;
  verificationAmount = 0;
  verificationCurrency = 'SAR';
  errorMessage = '';

  returnUrl = '/my-booking';
  returnData: unknown = null;

  constructor(@Inject(DOCUMENT) private document: Document) {}

  ngOnInit(): void {
    this.shared.setLoaded(true);
    this.loadStoredReturnData();

    this.route.queryParams.subscribe((params) => {
      const redirectType = params['type'];
      const paymentUuid = params['payment_uuid'];

      if (redirectType !== 'redirect_after_finish_payment' || !paymentUuid) {
        this.paymentState = 'error';
        this.errorMessage = 'THANK_YOU.states.error.invalidParams';
        return;
      }

      this.paymentUuid = paymentUuid;
      this.verifyPayment();
    });
  }

  get showCourtAction(): boolean {
    return this.returnUrl === '/court' && !!this.returnData;
  }

  goToMyBookings(): void {
    this.router.navigate(['/my-booking']);
  }

  goToCourt(): void {
    this.router.navigate(['/court'], {
      state: {
        data: this.returnData,
        refreshData: true,
      },
    });
  }

  goHome(): void {
    this.router.navigate(['/']);
  }

  private verifyPayment(): void {
    if (!this.paymentUuid) {
      this.paymentState = 'error';
      this.errorMessage = 'THANK_YOU.states.error.invalidParams';
      return;
    }

    this.paymentState = 'verifying';

    this.http
      .post<VerifyPaymentResponse>(`${environment.apiUrl}/customer/orders/verifyPayment`, {
        payment_uuid: this.paymentUuid,
      })
      .subscribe({
        next: (response) => {
          const responseData = response?.data || {};
          this.verificationAmount = this.toNumber(responseData.amount);
          this.verificationCurrency = responseData.currency || 'SAR';

          if (response?.status === 'success') {
            this.paymentState = 'success';
            this.pushPurchaseEvent();
          } else {
            this.paymentState = 'error';
            this.errorMessage = response?.message || 'THANK_YOU.states.error.generic';
          }

          this.clearStoredReturnData();
        },
        error: () => {
          this.paymentState = 'error';
          this.errorMessage = 'THANK_YOU.states.error.generic';
          this.clearStoredReturnData();
        },
      });
  }

  private pushPurchaseEvent(): void {
    if (!this.paymentUuid) {
      return;
    }

    const storage = this.getStorage();
    const dedupeKey = `purchase_event_sent_${this.paymentUuid}`;
    if (storage?.getItem(dedupeKey) === '1') {
      return;
    }

    const windowRef = this.document.defaultView as
      | (Window & { dataLayer?: Array<Record<string, unknown>> })
      | null;

    if (!windowRef) {
      return;
    }

    windowRef.dataLayer = windowRef.dataLayer || [];
    windowRef.dataLayer.push({
      event: 'purchase',
      ecommerce: {
        transaction_id: this.paymentUuid,
        value: this.verificationAmount,
        currency: this.verificationCurrency || 'SAR',
      },
    });

    storage?.setItem(dedupeKey, '1');
  }

  private loadStoredReturnData(): void {
    const storage = this.getStorage();
    const rawReturnData = storage?.getItem('payment_return_data');
    if (!rawReturnData) {
      return;
    }

    try {
      const parsed = JSON.parse(rawReturnData);
      this.returnUrl = parsed?.returnUrl || '/my-booking';
      this.returnData = parsed?.returnData ?? null;
    } catch {
      this.returnUrl = '/my-booking';
      this.returnData = null;
    }
  }

  private clearStoredReturnData(): void {
    this.getStorage()?.removeItem('payment_return_data');
  }

  private getStorage(): Storage | null {
    return this.document.defaultView?.localStorage ?? null;
  }

  private toNumber(value: number | string | undefined): number {
    if (typeof value === 'number') {
      return value;
    }

    if (typeof value === 'string') {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : 0;
    }

    return 0;
  }
}
